import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SITE } from "../../../lib/siteData";

export default function HeroSection() {
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.15], [0, -60]);

  return (
    <section className="relative h-[92vh] min-h-[640px] overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: heroY }}>
        <video
          src={SITE.hero_video}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        />
        {/* Subtle bottom fade only — keeps the video visible */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#011233]/55" />
        {/* Left-side navy scrim — improves hero copy contrast against bright frames */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#011233]/70 via-[#011233]/30 to-transparent" />
        {/* Focused radial behind hero copy */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 55% at 28% 50%, rgba(1, 18, 51,0.55) 0%, rgba(1, 18, 51,0.25) 45%, rgba(1, 18, 51,0) 75%)",
          }}
        />
      </motion.div>

      <div className="relative h-full max-w-7xl mx-auto px-6 sm:px-10 flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <p
            className="text-[12px] uppercase tracking-[0.36em] text-white font-semibold drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]"
            data-testid="hero-eyebrow"
          >
            An Independent Insurance Agency
          </p>
          <h1
            className="mt-6 text-white font-heading font-bold tracking-tight text-5xl sm:text-7xl lg:text-[88px] leading-[0.95] drop-shadow-[0_4px_24px_rgba(0,0,0,0.65)]"
            data-testid="hero-title"
          >
            Protect what
            <br />
            matters most.
          </h1>
          <p
            className="mt-7 text-white text-lg sm:text-xl max-w-xl leading-relaxed drop-shadow-[0_2px_12px_rgba(0,0,0,0.65)]"
            data-testid="hero-tagline"
          >
            Your security is our top priority — one conversation, multiple carriers,
            the right coverage at the right price.
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <a
              href="#quote"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#011233] px-7 py-3.5 text-base font-semibold hover:bg-[#B5BAC2] transition-all duration-300 active:scale-95 shadow-lg"
              data-testid="hero-cta-quote"
            >
              Get A Quote
              <ArrowRight size={18} />
            </a>
            <a
              href="#services"
              className="inline-flex items-center gap-2 text-white text-base font-semibold hover:text-[#B5BAC2] transition-colors drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]"
              data-testid="hero-cta-services"
            >
              Explore coverage
              <ArrowRight size={16} />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
